/**
 * "New opportunity" form dialog. Minimal required fields per spec
 * section 6.5 (companyName, opportunityName, oppType, source). The
 * rest of the editing flow happens inline on the card or in the
 * slide-over.
 */

import { useState, type FormEvent } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { XMarkIcon } from '@heroicons/react/24/outline';

import { useCreateOpportunity } from '@/lib/api/sales';
import type { CreateOpportunityInput } from '@workspace/shared';

const OPP_TYPES: CreateOpportunityInput['oppType'][] = [
  'PROPOSAL',
  'TENDER',
  'GRANT',
  'VARIATION',
  'OTHER',
];

const SOURCES: CreateOpportunityInput['source'][] = [
  'KNOWN_CONTACT',
  'REFERRAL',
  'INBOUND',
  'OUTBOUND',
  'EVENT',
  'TENDER',
  'GRANT',
  'PROJECT',
];

export function NewOpportunityDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const createOpportunity = useCreateOpportunity();
  const [companyName, setCompanyName] = useState('');
  const [opportunityName, setOpportunityName] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [oppType, setOppType] = useState<CreateOpportunityInput['oppType']>('PROPOSAL');
  const [source, setSource] = useState<CreateOpportunityInput['source']>('INBOUND');
  const [budgetFee, setBudgetFee] = useState('');

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!companyName.trim() || !opportunityName.trim() || createOpportunity.isPending) return;
    await createOpportunity.mutateAsync({
      companyName: companyName.trim(),
      opportunityName: opportunityName.trim(),
      contactName: contactName.trim() || undefined,
      contactEmail: contactEmail.trim() || undefined,
      contactPhone: contactPhone.trim() || undefined,
      oppType,
      source,
      ...(budgetFee.trim() ? { budgetFee: parseFloat(budgetFee).toFixed(2) } : {}),
    });
    setCompanyName('');
    setOpportunityName('');
    setContactName('');
    setContactEmail('');
    setContactPhone('');
    setOppType('PROPOSAL');
    setSource('INBOUND');
    setBudgetFee('');
    onClose();
  }

  return (
    <Dialog.Root open={open} onOpenChange={(next) => (next ? null : onClose())}>
      <Dialog.Portal>
        <Dialog.Overlay aria-hidden className="fixed inset-0 z-40 bg-black/55" />
        <Dialog.Content
          aria-label="New opportunity"
          className="fixed left-1/2 top-1/2 z-50 w-[480px] max-w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 border border-mx-grey-800 bg-mx-card p-mx-5"
        >
          <div className="flex items-center justify-between">
            <Dialog.Title className="font-display text-mx-h2 font-semibold text-mx-white">
              New opportunity
            </Dialog.Title>
            <Dialog.Close
              aria-label="Close"
              className="flex h-7 w-7 items-center justify-center text-mx-grey-mid hover:text-mx-white"
            >
              <XMarkIcon className="h-5 w-5" strokeWidth={1.6} aria-hidden />
            </Dialog.Close>
          </div>
          <Dialog.Description className="sr-only">
            Create a new opportunity with company name, opportunity name, type, source, and optional
            budget.
          </Dialog.Description>
          <form className="mt-mx-4 flex flex-col gap-mx-3" onSubmit={onSubmit}>
            <label className="flex flex-col gap-mx-1">
              <span className="mono-label text-mx-grey-mid">COMPANY NAME</span>
              <input
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                required
                className="h-9 border border-mx-grey-800 bg-transparent px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-mx-1">
              <span className="mono-label text-mx-grey-mid">OPPORTUNITY NAME</span>
              <input
                value={opportunityName}
                onChange={(e) => setOpportunityName(e.target.value)}
                required
                className="h-9 border border-mx-grey-800 bg-transparent px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
              />
            </label>
            <div className="grid grid-cols-2 gap-mx-3">
              <label className="flex flex-col gap-mx-1">
                <span className="mono-label text-mx-grey-mid">CONTACT NAME</span>
                <input
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  className="h-9 border border-mx-grey-800 bg-transparent px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
                />
              </label>
              <label className="flex flex-col gap-mx-1">
                <span className="mono-label text-mx-grey-mid">CONTACT EMAIL</span>
                <input
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  type="email"
                  className="h-9 border border-mx-grey-800 bg-transparent px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
                />
              </label>
            </div>
            <label className="flex flex-col gap-mx-1">
              <span className="mono-label text-mx-grey-mid">CONTACT NUMBER</span>
              <input
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                type="tel"
                className="h-9 border border-mx-grey-800 bg-transparent px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
              />
            </label>
            <div className="grid grid-cols-2 gap-mx-3">
              <label className="flex flex-col gap-mx-1">
                <span className="mono-label text-mx-grey-mid">TYPE</span>
                <select
                  value={oppType}
                  onChange={(e) => setOppType(e.target.value as CreateOpportunityInput['oppType'])}
                  className="h-9 border border-mx-grey-800 bg-transparent px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
                >
                  {OPP_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-mx-1">
                <span className="mono-label text-mx-grey-mid">SOURCE</span>
                <select
                  value={source}
                  onChange={(e) => setSource(e.target.value as CreateOpportunityInput['source'])}
                  className="h-9 border border-mx-grey-800 bg-transparent px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
                >
                  {SOURCES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label className="flex flex-col gap-mx-1">
              <span className="mono-label text-mx-grey-mid">BUDGET FEE (OPTIONAL)</span>
              <input
                value={budgetFee}
                onChange={(e) => setBudgetFee(e.target.value)}
                type="number"
                min="0"
                step="100"
                className="h-9 border border-mx-grey-800 bg-transparent px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
              />
            </label>
            <div className="mt-mx-3 flex justify-end gap-mx-2">
              <button
                type="button"
                onClick={onClose}
                className="text-mx-mono text-mx-grey-mid hover:text-mx-white"
              >
                CANCEL
              </button>
              <button
                type="submit"
                disabled={
                  !companyName.trim() || !opportunityName.trim() || createOpportunity.isPending
                }
                className="h-9 bg-mx-orange px-mx-3 text-mx-body-sm font-medium text-mx-white hover:bg-mx-orange-hover disabled:bg-mx-grey-mid"
              >
                {createOpportunity.isPending ? 'Saving…' : 'Create opportunity'}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
